import { z } from 'zod';
import { CustomFieldType } from './enums.js';
import { IsoDate, IsoDateTime, Uuid, Version, boundedList } from './common.js';

const ProjectRefShape = z.object({ id: Uuid, key: z.string(), name: z.string() });

/** Option colors share the label palette. */
export const FieldOptionColor = z.enum(['gray', 'red', 'orange', 'amber', 'green', 'teal', 'blue', 'indigo', 'purple', 'pink']);

/** A select option: the id is stable, so renaming an option keeps every stored value. */
export const FieldOption = z.object({ id: z.string().min(1).max(40), label: z.string().min(1).max(60), color: FieldOptionColor });
export type FieldOption = z.infer<typeof FieldOption>;
export const FieldOptionInput = z.object({ id: z.string().min(1).max(40).optional(), label: z.string().trim().min(1).max(60), color: FieldOptionColor.optional() }).strict();

export const CustomFieldView = z.object({
  id: Uuid,
  /** GLOBAL applies to all work; PROJECT to the work of one project; DOCUMENTS to documents (ADR-0042). */
  scope: z.enum(['GLOBAL', 'PROJECT', 'DOCUMENTS']),
  project: ProjectRefShape.nullable(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  type: CustomFieldType,
  options: z.array(FieldOption),
  position: z.number().int(),
  archived: z.boolean(),
  version: Version,
  /** Whether the caller can rename, reorder or archive this definition. */
  canManage: z.boolean(),
  updatedAt: IsoDateTime,
});
export type CustomFieldView = z.infer<typeof CustomFieldView>;

export const CreateCustomField = z
  .object({
    name: z.string().trim().min(1).max(60),
    description: z.string().max(300).optional(),
    type: CustomFieldType,
    /** Omit for a field over all work (Product, Developers or administrators). */
    projectId: Uuid.optional(),
    /** DOCUMENTS defines a document property instead of a work field (ADR-0042). */
    scope: z.enum(['WORK', 'DOCUMENTS']).optional(),
    options: boundedList(FieldOptionInput, 50).optional(),
    position: z.number().int().min(0).max(1000).optional(),
  })
  .strict();
export type CreateCustomField = z.infer<typeof CreateCustomField>;

export const UpdateCustomField = z
  .object({
    expectedVersion: Version,
    name: z.string().trim().min(1).max(60).optional(),
    description: z.string().max(300).optional(),
    /** Replaces the options; options left out are removed from new choices, stored values stay. */
    options: boundedList(FieldOptionInput, 50).optional(),
    position: z.number().int().min(0).max(1000).optional(),
    /** Archiving hides the field from forms and lists; values are kept and come back on restore. */
    archived: z.boolean().optional(),
  })
  .strict();
export type UpdateCustomField = z.infer<typeof UpdateCustomField>;

/**
 * A stored value: text, number, yes/no, calendar day, one option id, or several option ids. null
 * clears the field.
 */
export const CustomFieldValue = z.union([z.string().max(500), z.number().finite(), z.boolean(), z.array(z.string().max(40)).max(20)]);
export type CustomFieldValue = z.infer<typeof CustomFieldValue>;

/** Values keyed by field id: in views every field with a value; in updates only the fields that change. */
export const CustomFieldValues = z.record(Uuid, CustomFieldValue);
export const CustomFieldPatch = z.record(Uuid, CustomFieldValue.nullable());
export type CustomFieldPatch = z.infer<typeof CustomFieldPatch>;

export const CustomFieldListQuery = z.object({
  /** Fields that apply to this project's work (global ones included). Without it: every field the caller can see. */
  projectId: Uuid.optional(),
  includeArchived: z.enum(['true']).optional(),
  /** DOCUMENTS lists document properties; without it, work fields only (ADR-0042). */
  scope: z.enum(['WORK', 'DOCUMENTS']).optional(),
});

/** Calendar day check reused by the service for DATE fields. */
export const CustomDateValue = IsoDate;
